import { useState } from "react";
import intl from "react-intl-universal";
import { useHistory } from "react-router-dom";
import { TbCheck } from "react-icons/tb";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { BaasInstanceEnvironmentBadge } from "../Instances/components/BaasInstanceEnvironmentBadge";
import { BaasKybStatusBadge } from "../Instances/components/BaasKybStatusBadge";

const STEPS = [
  { id: "company", labelKey: "baas.kyb.stepCompany" },
  { id: "address", labelKey: "baas.kyb.stepAddress" },
  { id: "documents", labelKey: "baas.kyb.stepDocuments" },
  { id: "review", labelKey: "baas.kyb.stepReview" },
];

const inputClass =
  "h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] focus:ring-2 focus:ring-[#e7a73f]/20 dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100";

const Field = ({ label, value, onChange, type = "text", required = false }) => (
  <div>
    <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">
      {label}
    </label>
    <input
      type={type}
      className={inputClass}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      required={required}
    />
  </div>
);

export const BaasKybPage = () => {
  const history = useHistory();
  const { selectedInstance, updateInstance } = useBaasInstance();
  const currentStatus = selectedInstance?.kybStatus ?? "pending";
  const [stepIndex, setStepIndex] = useState(0);
  const [submitted, setSubmitted] = useState(
    currentStatus === "in_review" || currentStatus === "approved",
  );
  const [form, setForm] = useState({
    legalName: selectedInstance?.name ?? "",
    taxId: "",
    formationDate: "",
    email: "",
    phone: "",
    street: "",
    city: "",
    state: "",
    zipCode: "",
  });

  if (!selectedInstance) {
    return null;
  }

  const currentStep = STEPS[stepIndex];
  const isLastStep = stepIndex === STEPS.length - 1;

  const handleSubmit = () => {
    updateInstance(selectedInstance.id, {
      kybStatus: "in_review",
      kyb: { ...form, submittedAt: new Date().toISOString() },
    });
    setSubmitted(true);
  };

  const handleContinueToConsole = () => {
    history.push("/baas/inicio");
  };

  return (
    <div className="baas-kyb mx-auto min-w-0 max-w-2xl">
      <header className="mb-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-brand-dark dark:text-gray-100">
              {intl.get("baas.kyb.title")}
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {intl.get("baas.kyb.subtitle")}
            </p>
          </div>
          <BaasKybStatusBadge
            status={submitted ? "in_review" : currentStatus}
            hideWhenApproved={false}
            className="text-xs"
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-cream text-xs font-bold text-brand-dark dark:bg-brand-dark-elevated dark:text-gray-100">
            {selectedInstance.initials}
          </div>
          <span className="text-sm font-semibold text-brand-dark dark:text-gray-100">
            {selectedInstance.name}
          </span>
          <BaasInstanceEnvironmentBadge environment={selectedInstance.environment} />
        </div>
      </header>

      <div className="app-card overflow-hidden">
        {submitted ? (
          <div className="px-6 py-10 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
              <TbCheck className="h-7 w-7" />
            </div>
            <h2 className="text-lg font-bold text-brand-dark dark:text-gray-100">
              {intl.get("baas.kyb.submittedTitle")}
            </h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-gray-500 dark:text-gray-400">
              {intl.get("baas.kyb.submittedDescription")}
            </p>
            <button type="button" className="btn-primary mt-6" onClick={handleContinueToConsole}>
              {intl.get("baas.kyb.continueToConsole")}
            </button>
          </div>
        ) : (
          <>
            <div className="border-b border-gray-100 px-6 py-4 dark:border-brand-dark-border">
              <div className="flex gap-2">
                {STEPS.map((step, index) => (
                  <div
                    key={step.id}
                    className={`h-1 flex-1 rounded-full ${
                      index <= stepIndex ? "bg-[#e7a73f]" : "bg-gray-200 dark:bg-brand-dark-border"
                    }`}
                  />
                ))}
              </div>
              <p className="mt-2 text-xs font-semibold text-gray-500 dark:text-gray-400">
                {intl.get(currentStep.labelKey)} ({stepIndex + 1}/{STEPS.length})
              </p>
            </div>

            <div className="space-y-4 px-6 py-5">
              {currentStep.id === "company" && (
                <>
                  <Field
                    label={intl.get("baas.kyb.legalName")}
                    value={form.legalName}
                    onChange={(value) => setForm((prev) => ({ ...prev, legalName: value }))}
                    required
                  />
                  <Field
                    label={intl.get("baas.kyb.taxId")}
                    value={form.taxId}
                    onChange={(value) => setForm((prev) => ({ ...prev, taxId: value }))}
                    required
                  />
                  <Field
                    label={intl.get("baas.kyb.formationDate")}
                    value={form.formationDate}
                    onChange={(value) => setForm((prev) => ({ ...prev, formationDate: value }))}
                    type="date"
                  />
                  <Field
                    label={intl.get("baas.kyb.email")}
                    value={form.email}
                    onChange={(value) => setForm((prev) => ({ ...prev, email: value }))}
                    type="email"
                  />
                  <Field
                    label={intl.get("baas.kyb.phone")}
                    value={form.phone}
                    onChange={(value) => setForm((prev) => ({ ...prev, phone: value }))}
                  />
                </>
              )}

              {currentStep.id === "address" && (
                <>
                  <Field
                    label={intl.get("baas.kyb.street")}
                    value={form.street}
                    onChange={(value) => setForm((prev) => ({ ...prev, street: value }))}
                  />
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field
                      label={intl.get("baas.kyb.city")}
                      value={form.city}
                      onChange={(value) => setForm((prev) => ({ ...prev, city: value }))}
                    />
                    <Field
                      label={intl.get("baas.kyb.state")}
                      value={form.state}
                      onChange={(value) => setForm((prev) => ({ ...prev, state: value }))}
                    />
                  </div>
                  <Field
                    label={intl.get("baas.kyb.zipCode")}
                    value={form.zipCode}
                    onChange={(value) => setForm((prev) => ({ ...prev, zipCode: value }))}
                  />
                </>
              )}

              {currentStep.id === "documents" && (
                <div className="rounded-xl border border-dashed border-gray-200 p-8 text-center dark:border-brand-dark-border">
                  <p className="text-sm font-medium text-brand-dark dark:text-gray-100">
                    {intl.get("baas.kyb.documentsTitle")}
                  </p>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    {intl.get("baas.kyb.documentsPlaceholder")}
                  </p>
                </div>
              )}

              {currentStep.id === "review" && (
                <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                  <p>
                    <span className="font-semibold">{intl.get("baas.kyb.legalName")}:</span>{" "}
                    {form.legalName || "—"}
                  </p>
                  <p>
                    <span className="font-semibold">{intl.get("baas.kyb.taxId")}:</span>{" "}
                    {form.taxId || "—"}
                  </p>
                  <p>
                    <span className="font-semibold">{intl.get("baas.kyb.formationDate")}:</span>{" "}
                    {form.formationDate || "—"}
                  </p>
                  <p>
                    <span className="font-semibold">{intl.get("baas.kyb.email")}:</span>{" "}
                    {form.email || "—"}
                  </p>
                  <p>
                    <span className="font-semibold">{intl.get("baas.kyb.street")}:</span>{" "}
                    {form.street || "—"}
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-between border-t border-gray-100 px-6 py-4 dark:border-brand-dark-border">
              <button
                type="button"
                className="btn-outline"
                onClick={() =>
                  stepIndex === 0 ? history.push("/baas") : setStepIndex((index) => index - 1)
                }
              >
                {stepIndex === 0 ? intl.get("common.cancel") : intl.get("nav.back")}
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => (isLastStep ? handleSubmit() : setStepIndex((index) => index + 1))}
                disabled={currentStep.id === "company" && !form.legalName.trim()}
              >
                {isLastStep ? intl.get("baas.kyb.submit") : intl.get("firstAccess.continue")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
